<template>
	<div class="rounded-lg border border-primary/20 bg-primary/5 p-3 text-xs">
		<div v-if="hasThemeInfo" class="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1">
			<div v-if="setting.primary_color" class="inline-flex items-center gap-1.5">
				<span class="text-surface-500">{{ t("content.common_sense.primary_color") }}</span>
				<span
					class="inline-block h-4 w-4 shrink-0 rounded border border-surface-300"
					:style="{ backgroundColor: setting.primary_color }"
				/>
				<span class="font-mono text-surface-700 dark:text-surface-200">{{ setting.primary_color }}</span>
			</div>
			<span v-if="setting.primary_color && fontLabel" class="hidden text-surface-300 sm:inline">·</span>
			<div v-if="fontLabel" class="inline-flex items-center gap-1.5">
				<span class="text-surface-500">{{ t("content.common_sense.font_family") }}</span>
				<span class="font-medium text-surface-800 dark:text-surface-100">{{ fontLabel }}</span>
			</div>
			<div v-if="setting.logo" class="inline-flex items-center gap-1.5">
				<span class="text-surface-500">{{ t("content.common_sense.event_logo") }}</span>
				<img
					:src="settingLogoUrl"
					:alt="t('content.common_sense.event_logo')"
					class="h-8 w-8 rounded border border-surface-300 object-contain bg-white"
				/>
			</div>
		</div>

		<div class="flex flex-wrap items-center gap-x-3 gap-y-1.5">
			<span
				v-for="flag in personalizationFlags"
				:key="flag.key"
				class="inline-flex items-center gap-1"
			>
				<i
					class="shrink-0 text-[11px] font-bold"
					:class="flag.enabled ? 'pi pi-check text-green-600' : 'pi pi-times text-red-500'"
					:aria-label="flag.enabled ? t('general.yes') : t('general.no')"
				/>
				<span class="text-surface-700 dark:text-surface-200">{{ flag.label }}</span>
			</span>
			<span v-if="clientLanguageLabel" class="inline-flex items-center gap-1 text-surface-600">
				<span class="text-surface-500">{{ t("content.common_sense.client_language") }}:</span>
				<span class="font-medium text-surface-800 dark:text-surface-100">{{ clientLanguageLabel }}</span>
			</span>
		</div>

		<div v-if="customizeUrl" class="mt-2 border-t border-primary/10 pt-2">
			<a
				:href="customizeUrl"
				target="_blank"
				rel="noopener noreferrer"
				class="inline-flex max-w-full items-center gap-1 text-primary hover:underline"
				:title="customizeUrl"
			>
				<i class="pi pi-external-link shrink-0 text-[10px]" />
				<span class="truncate">{{ t("customization.common_sense.public_link") }}</span>
			</a>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { computed } from "vue"
import { useStoreI18n } from "@/composables/useStoreI18n"
import { buildCommonSenseCustomizeUrl, COMMON_SENSE_FONT_OPTIONS } from "@/constants/common_sense_cms"
import { getLanguagesText } from "@/lang/config"
import { resolveApiMediaUrl } from "@/utils/fetchMediaAsFile"
import type { CommonSenseSetting } from "@/stores/apps/interfaces/common_sense_interfaces"

const props = defineProps<{
	setting: CommonSenseSetting
}>()

const { t } = useStoreI18n()

const fontLabel = computed(() => {
	const raw = props.setting.font_family
	if (!raw) return null
	return COMMON_SENSE_FONT_OPTIONS.find((option) => option.value === raw)?.label ?? raw
})

const clientLanguageLabel = computed(() => {
	const lang = props.setting.client_language
	if (!lang) return null
	const languages = getLanguagesText()
	return languages[lang as keyof typeof languages] ?? String(lang).toUpperCase()
})

const hasThemeInfo = computed(
	() => Boolean(props.setting.primary_color || props.setting.font_family || props.setting.logo)
)

const settingLogoUrl = computed(() => resolveApiMediaUrl(props.setting.logo ?? null))

const personalizationFlags = computed(() => [
	{
		key: "theme_editable",
		label: t("content.common_sense.theme_editable"),
		enabled: Boolean(props.setting.theme_editable),
	},
	{
		key: "questions_editable",
		label: t("content.common_sense.questions_editable"),
		enabled: Boolean(props.setting.questions_editable),
	},
	{
		key: "questions_choose",
		label: t("content.common_sense.questions_choose"),
		enabled: Boolean(props.setting.questions_choose),
	},
])

const customizeUrl = computed(() => {
	if (!props.setting.custom_uuid) return null
	return buildCommonSenseCustomizeUrl(String(props.setting.custom_uuid))
})
</script>
